import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedListing, setSelectedListing] = useState(null);
  const [showListingModal, setShowListingModal] = useState(false);

  useEffect(() => {
    fetchListings();
  }, [statusFilter]);

  const fetchListings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API}/admin/listings`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (listingId, action) => {
    let reason = '';
    if (action === 'reject' || action === 'remove') {
      reason = prompt(`Enter reason for ${action}:`);
      if (!reason) return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/listings/${listingId}/moderate?action=${action}&reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Listing ${action} successful`);
      fetchListings();
      setShowListingModal(false);
    } catch (error) {
      console.error('Failed to moderate listing:', error);
      toast.error('Failed to moderate listing');
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to permanently delete this listing?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing deleted successfully');
      fetchListings();
      setShowListingModal(false);
    } catch (error) {
      console.error('Failed to delete listing:', error);
      toast.error('Failed to delete listing');
    }
  };

  const viewListingDetails = async (listingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedListing(response.data);
      setShowListingModal(true);
    } catch (error) {
      console.error('Failed to fetch listing details:', error);
      toast.error('Failed to load listing details');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Listings</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="removed">Removed</option>
              </select>
            </div>

            <div className="text-right">
              <p className="text-sm text-gray-600">Total Listings</p>
              <p className="text-2xl font-bold text-blue-600">{listings.length}</p>
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <div key={listing.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              {/* Image */}
              <div className="h-48 bg-gray-200 relative">
                {listing.images && listing.images.length > 0 ? (
                  <img
                    src={listing.images[0]}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <span className="text-5xl">🏠</span>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    listing.status === 'active' ? 'bg-green-500 text-white' :
                    listing.status === 'pending' ? 'bg-yellow-500 text-white' :
                    listing.status === 'rejected' ? 'bg-red-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}>
                    {listing.status}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-bold text-lg mb-1 truncate">{listing.title}</h3>
                <p className="text-blue-600 font-bold text-xl mb-2">R{listing.price?.toLocaleString()}</p>
                <p className="text-sm text-gray-600 mb-2">{listing.type} - {listing.propertyType}</p>
                
                {listing.ownerInfo && (
                  <div className="text-xs text-gray-500 mb-3">
                    <p>Owner: {listing.ownerInfo.fullName}</p>
                    <p>{listing.ownerInfo.email}</p>
                  </div>
                )}

                <button
                  onClick={() => viewListingDetails(listing.id)}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {listings.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">No listings found</p>
          </div>
        )}

        {/* Listing Detail Modal */}
        {showListingModal && selectedListing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold">Listing Details</h3>
                <button
                  onClick={() => setShowListingModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Images */}
                {selectedListing.images && selectedListing.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedListing.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Property ${idx + 1}`}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                )}

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Title</p>
                    <p className="font-medium">{selectedListing.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Price</p>
                    <p className="font-medium text-blue-600">R{selectedListing.price?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Type</p>
                    <p className="font-medium">{selectedListing.type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Property Type</p>
                    <p className="font-medium">{selectedListing.propertyType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-medium">{selectedListing.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Messages</p>
                    <p className="font-medium">{selectedListing.messagesCount || 0}</p>
                  </div>
                </div>

                {/* Owner Info */}
                {selectedListing.ownerInfo && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Owner Information</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="text-gray-600">Name:</span> {selectedListing.ownerInfo.fullName}</p>
                      <p><span className="text-gray-600">Email:</span> {selectedListing.ownerInfo.email}</p>
                      <p><span className="text-gray-600">Role:</span> {selectedListing.ownerInfo.userMode}</p>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <p className="text-gray-800">{selectedListing.description}</p>
                </div>

                {/* Specifications */}
                {selectedListing.specifications && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Specifications</p>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <p>🛏️ Beds: {selectedListing.specifications.beds}</p>
                      <p>🚿 Baths: {selectedListing.specifications.baths}</p>
                      <p>📐 Size: {selectedListing.specifications.propertySize} sqm</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t bg-gray-50 flex flex-wrap gap-3">
                {selectedListing.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleModerate(selectedListing.id, 'approve')}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleModerate(selectedListing.id, 'reject')}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </>
                )}

                {selectedListing.isFeatured ? (
                  <button
                    onClick={() => handleModerate(selectedListing.id, 'unfeature')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
                  >
                    Unfeature
                  </button>
                ) : (
                  <button
                    onClick={() => handleModerate(selectedListing.id, 'feature')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
                  >
                    Feature Listing
                  </button>
                )}

                <button
                  onClick={() => handleModerate(selectedListing.id, 'remove')}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
                >
                  Remove
                </button>

                <button
                  onClick={() => handleDelete(selectedListing.id)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>

                <button
                  onClick={() => setShowListingModal(false)}
                  className="ml-auto bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}